/**
 * DiscountBanner — renders the active Stripe Discount applied to the
 * customer's subscription.
 *
 * Copy varies by ``duration``:
 *   forever   → "20% off every invoice (PROMO20)."
 *   repeating → "20% off for 6 months, then standard pricing."
 *   once      → "$25 off your next invoice, then standard pricing."
 *
 * When ``ends_at`` is set, appends " — expires [date]."
 * Promotion-code suffix is appended when present (in parens).
 */

import { Tag03 } from '@untitledui/icons';
import { useMemo } from 'react';

import { StatusBanner } from '@/lib/base/banners';
import { formatMoney } from '@/lib/billing/currency';
import type { DiscountInfo } from '@/lib/billing/types';

interface DiscountBannerProps {
  discount: DiscountInfo;
}

function formatAmount(discount: DiscountInfo): string {
  if (discount.percent_off !== null) {
    // Drop trailing .00 — "20% off" reads better than "20.00% off".
    const pct = Number(discount.percent_off);
    const pretty = Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(2);
    return `${pretty}% off`;
  }
  if (discount.amount_off_cents !== null && discount.currency) {
    return `${formatMoney(discount.amount_off_cents, discount.currency)} off`;
  }
  // Defensive fallback — shouldn't happen given Stripe's invariants.
  return 'Discount applied';
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function DiscountBanner({ discount }: DiscountBannerProps) {
  const { title, description } = useMemo(() => {
    const amount = formatAmount(discount);
    const codeSuffix = discount.promotion_code
      ? ` (${discount.promotion_code})`
      : '';
    const expiresSuffix = discount.ends_at
      ? ` – expires ${formatDate(discount.ends_at)}`
      : '';

    let headline: string;
    let body: string | undefined;

    switch (discount.duration) {
      case 'forever':
        headline = `You have ${amount} every invoice${codeSuffix}.`;
        body = expiresSuffix ? `Discount${expiresSuffix}.` : undefined;
        break;
      case 'repeating': {
        const months = discount.duration_in_months;
        const monthsCopy = months
          ? `${amount} for ${months} ${months === 1 ? 'month' : 'months'}`
          : `${amount}`;
        headline = `${monthsCopy}${codeSuffix}, then standard pricing.`;
        body = expiresSuffix ? `Discount${expiresSuffix}.` : undefined;
        break;
      }
      case 'once':
      default: {
        headline = `${amount} on your next invoice${codeSuffix}, then standard pricing.`;
        const couponBody = discount.coupon_name || undefined;
        if (expiresSuffix) {
          body = couponBody
            ? `${couponBody} – discount${expiresSuffix}.`
            : `Discount${expiresSuffix}.`;
        } else {
          body = couponBody;
        }
        break;
      }
    }

    return { title: headline, description: body };
  }, [discount]);

  return (
    <div data-component='billing-banner' data-instance='discount-applied'>
      <StatusBanner
        variant='info'
        icon={Tag03}
        title={title}
        description={description}
      />
    </div>
  );
}
